/**
 * Shared parameter system — the core of the families architecture.
 *
 * A ParamSchema is a pure, serialisable description of the parameters a family accepts.
 * resolveParams fills every key from defaults, overrides with provided partials, and clamps
 * numbers to min/max. The schema drives the auto-generated ParamControls UI; adding a
 * parameter to a schema makes a control appear with zero extra code.
 */

export type ParamDef =
  | {
      key: string
      type: 'number'
      label: string
      default: number
      min?: number
      max?: number
      step?: number
      unit?: 'ft' | 'in' | 'deg'
    }
  | {
      key: string
      type: 'enum'
      label: string
      default: string
      options: { value: string; label: string }[]
    }
  | {
      key: string
      type: 'bool'
      label: string
      default: boolean
    }

export type ParamSchema = ParamDef[]
export type ParamValues = Record<string, number | string | boolean>

export type StyleId = 'budget' | 'standard' | 'premium'

/**
 * Fill every key in `schema` from `partial` (or defaults when absent),
 * clamping numbers to min/max and validating enum values.
 */
export function resolveParams(schema: ParamSchema, partial?: Partial<ParamValues>): ParamValues {
  const result: ParamValues = {}
  for (const def of schema) {
    const raw = partial?.[def.key]
    if (def.type === 'number') {
      let v = typeof raw === 'number' ? raw : def.default
      if (def.min !== undefined) v = Math.max(def.min, v)
      if (def.max !== undefined) v = Math.min(def.max, v)
      result[def.key] = v
    } else if (def.type === 'enum') {
      const valid = def.options.map((o) => o.value)
      result[def.key] = typeof raw === 'string' && valid.includes(raw) ? raw : def.default
    } else {
      result[def.key] = typeof raw === 'boolean' ? raw : def.default
    }
  }
  return result
}

/** Convenience: read a number from resolved params (type-safe). */
export function pNum(params: ParamValues, key: string, fallback = 0): number {
  const v = params[key]
  return typeof v === 'number' ? v : fallback
}

/** Convenience: read a string from resolved params. */
export function pStr(params: ParamValues, key: string, fallback = ''): string {
  const v = params[key]
  return typeof v === 'string' ? v : fallback
}

/** Convenience: read a bool from resolved params. */
export function pBool(params: ParamValues, key: string, fallback = false): boolean {
  const v = params[key]
  return typeof v === 'boolean' ? v : fallback
}
